//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { MAX_BULK_PIN_LIMIT } from '@pinterest/redux/post-details/pinterestPostDetailsModel';

/**
 * Preconfigured content drawer settings for various areas of the Curalate platform,
 * previously found in the content-drawer-settings.js file.
 */
export const ContentDrawerSidebarOptions = {
  Reveal: {
    scope: 'Reveal',
    ctaText: 'Create a Reveal',
    multiSelect: false,
    contextualHelp: {
      icon: 'di di-information-16px',
      message: 'Only one reveal can be created at a time.',
    },
    uploadText: 'Upload and Reveal',
    options: [],
  },
  Like2Buy: {
    scope: 'Like2Buy',
    ctaText: 'Add to Like2Buy',
    multiSelect: true,
    uploadText: 'Upload and\nAdd to Like2Buy',
    options: [],
    filterOptions: {
      initialFilters: {
        distribution: ['s:pinterest', 's:instagram', 'like2buy'],
        isDistributed: ['false'],
      },
    },
  },
  Scheduler: {
    scope: 'Scheduler',
    ctaText: 'Schedule',
    multiSelect: true,
    uploadText: 'Upload and Schedule',
    contextualHelp: {
      icon: 'di di-information-16px',
      message:
        "By default, we've filtered your Library to show only unscheduled items.",
      hideOnFilterChange: true,
    },
    options: [
      {
        name: 'Unscheduled',
        // Used locally only - this is converted to the equivalent 'isScheduled' value by the content-drawer component
        // i.e. Unscheduled Only = true -> isScheduled = false, Unscheduled Only = false -> isScheduled = null
        key: 'unscheduledOnly',
      },
    ],
    filterOptions: {
      initialFilters: {
        distribution: ['s:pinterest', 's:instagram'],
        isDistributed: ['false'],
      },
    },
  },
  Share: {
    disableContentDrawer: true,
    disableImageUploader: true,
  },
  ShareV2: {
    disableContentDrawer: true,
    enableToastNotification: true,
    useLegacyImageUploaderStyle: true,
  },
  Library: {
    disableContentDrawer: true,
  },
  Instagram: {
    contextualHelp: {
      hideOnFilterChange: true,
      icon: 'di di-information-16px',
      message:
        "By default, we've filtered your Library to show only unscheduled items.",
    },
    ctaText: 'Schedule',
    filterOptions: {
      initialFilters: {
        distribution: ['s:pinterest', 's:instagram'],
        isDistributed: ['false'],
      },
    },
    multiSelect: false,
    options: [
      {
        // Used locally only - this is converted to the equivalent "isScheduled" value by the content-drawer component
        // i.e. Unscheduled Only = true -> isScheduled = false, Unscheduled Only = false -> isScheduled = null
        key: 'unscheduledOnly',
        name: 'Unscheduled',
      },
    ],
    scope: 'Scheduler',
    uploadText: 'Upload and Schedule',
  },
  Report: {
    disableContentDrawer: true,
  },
  FacebookImage: {
    contextualHelp: {
      icon: 'di di-information-16px',
      message:
        "We've filtered out videos and GIFs. You may only schedule one type of media at a time to Facebook.",
    },
    ctaText: 'Schedule',
    filterOptions: {
      initialFilters: {
        mediaItemTypes: ['Image'],
      },
      excludedSummaryKeys: ['mediaItemTypes'],
      lockedKeys: ['mediaItemTypes'],
    },
    multiSelect: true,
  },
  FacebookNoMediaAdded: {
    ctaText: 'Add Media',
    multiSelect: true,
  },
  FacebookVideo: {
    ctaText: 'Schedule',
    filterOptions: {
      initialFilters: {
        mediaItemTypes: ['Video'],
      },
      excludedSummaryKeys: ['mediaItemTypes'],
      lockedKeys: ['mediaItemTypes'],
    },
    multiSelect: false,
  },
  InstagramPostDetailsCreate: {
    filterOptions: {
      initialFilters: {
        distribution: ['s:instagram'],
        isDistributed: ['false'],
      },
    },
  },
  InstagramPostDetailsEdit: {
    ctaText: 'Add Media',
    multiSelect: true,
    filterOptions: {
      initialFilters: {
        distribution: ['s:instagram'],
        isDistributed: ['false'],
      },
    },
  },
  InstagramStoryPostDetails: {
    ctaText: 'Add Media',
    multiSelect: true,
    filterOptions: {
      initialFilters: {
        distribution: ['s:instagram'],
        isDistributed: ['false'],
        mediaItemTypes: ['Image', 'Video'],
      },
      excludedSummaryKeys: ['mediaItemTypes'],
      lockedKeys: ['mediaItemTypes'],
    },
  },
  InstagramReelsPostDetails: {
    ctaText: 'Add Media',
    multiSelect: false,
    filterOptions: {
      initialFilters: {
        distribution: ['s:instagram'],
        isDistributed: ['false'],
        mediaItemTypes: ['Video'],
      },
      excludedSummaryKeys: ['mediaItemTypes'],
      lockedKeys: ['mediaItemTypes'],
    },
  },
  CreatorsHarmonyAiSearch: {
    ctaText: 'Select Media',
    multiSelect: false,
    filterOptions: {
      initialFilters: {
        mediaItemTypes: ['Image'],
      },
      excludedSummaryKeys: ['mediaItemTypes'],
      lockedKeys: ['mediaItemTypes'],
    },
  },
  Like2BuyV2: {
    scope: 'Like2Buy',
    ctaText: 'Add to Like2Buy',
    multiSelect: true,
    uploadText: 'Upload and\nAdd to Like2Buy',
    options: [],
  },
  TikTokLike2Buy: {
    scope: 'TikTok Like2Buy',
    ctaText: 'Add to Like2Buy',
    multiSelect: true,
    uploadText: 'Upload and\nAdd to Like2Buy',
    options: [],
  },
  TikTokVideoAutomaticPostDetails: {
    ctaText: 'Add Media',
    multiSelect: false,
    filterOptions: {
      initialFilters: {
        mediaItemTypes: ['Video'],
      },
      excludedSummaryKeys: ['mediaItemTypes'],
      lockedKeys: ['mediaItemTypes'],
    },
  },
  TwitterPostDetailsNoMediaAdded: {
    ctaText: 'Add Media',
    multiSelect: true,
  },
  TwitterPostDetailsImageGallery: {
    ctaText: 'Add Media',
    multiSelect: true,
    filterOptions: {
      initialFilters: {
        mediaItemTypes: ['Image'],
      },
      excludedSummaryKeys: ['mediaItemTypes'],
      lockedKeys: ['mediaItemTypes'],
    },
    contextualHelp: {
      icon: 'di di-information-16px',
      message:
        "We've filtered out videos and GIFs. You may only schedule one type of media at a time to Twitter.",
    },
  },
  PinterestPostDetails: {
    ctaText: 'Add Media',
    multiSelect: false,
    filterOptions: {
      initialFilters: {
        distribution: ['s:pinterest'],
        isDistributed: ['false'],
      },
    },
  },
  PinterestPostDetailsCarousel: {
    ctaText: 'Add Media',
    multiSelect: false,
    filterOptions: {
      initialFilters: {
        distribution: ['s:pinterest'],
        isDistributed: ['false'],
        mediaItemTypes: ['Image'],
      },
      excludedSummaryKeys: ['mediaItemTypes'],
      lockedKeys: ['mediaItemTypes'],
    },
    contextualHelp: {
      icon: 'di di-information-16px',
      message:
        "We've filtered out videos and GIFs. You may only schedule one type of media at a time to Pinterest.",
    },
  },
  PinterestBulkSchedule: {
    ctaText: 'Add Pins',
    multiSelect: true,
    multiSelectLimit: MAX_BULK_PIN_LIMIT,
    filterOptions: {
      initialFilters: {
        distribution: ['s:pinterest'],
        isDistributed: ['false'],
      },
    },
  },
} as const;
